import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { userLogout } from '@uncinc/react-drupal-core';

import { Button, Popup } from '../../components';
import { userRemoveAccount } from '../../store/actions/User';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import {
  warningConfigs,
  type WarningMessageId,
} from '../../config/buddyCoScreeningQuestions';

type Props = {
  warningId: WarningMessageId;
  onClose: () => void;
};

const WarningModal: React.FC<Props> = ({ warningId, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const { externalUrl } = warningConfigs[warningId];

  const linkComponents = useMemo(() => {
    const bodyLinks = t(`buddy_co_screening.warnings.${warningId}.body_links`, {
      returnObjects: true,
      defaultValue: {},
    });

    return Object.fromEntries(
      Object.entries(bodyLinks).map(([key, url]) => [
        key,
        /* eslint-disable jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content */
        <a key={key} href={url} target="_blank" rel="noreferrer" />,
        /* eslint-enable jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content */
      ]),
    );
  }, [t, warningId]);

  const handleDeleteAccount = () => {
    dispatch(userRemoveAccount());
    dispatch(userLogout());
  };

  return (
    <Popup
      title={t(`buddy_co_screening.warnings.${warningId}.title`)}
      onClose={onClose}
      closeButtonText={t('buddy_co_screening.warnings.continue')}
      cta={(
        <Button warning onClick={handleDeleteAccount}>
          {t('buddy_co_screening.warnings.delete_account')}
        </Button>
      )}
    >
      <p>
        <Trans
          i18nKey={`buddy_co_screening.warnings.${warningId}.body`}
          components={linkComponents}
        />
        {externalUrl && (
          <>
            {' '}
            <a href={externalUrl} target="_blank" rel="noreferrer">
              {t(`buddy_co_screening.warnings.${warningId}.external_label`)}
            </a>.
          </>
        )}
      </p>
    </Popup>
  );
};

export default WarningModal;
